export const MONTHLY_BILLING_CYCLE = 'monthly';
export const YEARLY_BILLING_CYCLE = 'yearly';
export const PRO_PLAN_KEY = 'pro';
export const MAX_PLAN_KEY = 'max';

const MARKET_PRO_OFFER_KEY = 'monthly-market-pro';
const PRO_50_12_OFFER_KEY = 'pro50x12';
const GET_MAX_50_OFFER_KEY = 'getmax50';
const HERO_MAX_30_OFFER_KEY = 'heromax30';
const HERO_MAX_50_OFFER_KEY = 'heromax50';
const ANNUAL_50_OFFER_KEY = 'annual50';

export const normalizeSubscriptionInterval = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'yearly' || normalized === 'year') return YEARLY_BILLING_CYCLE;
  if (normalized === 'monthly' || normalized === 'month') return MONTHLY_BILLING_CYCLE;

  return null;
};

export const offerTargetsPlan = (offer, planKey) =>
  offer?.targetPlans?.includes(planKey) ?? false;

export const offerBillingLabel = (offer) =>
  offer?.billingCycle === YEARLY_BILLING_CYCLE ? 'annual' : 'monthly';

// Display rounding shared by the plan page and the offer summary card.
export const discountedAmount = (amount, percent) => {
  if (amount == null || percent == null) return null;

  return Math.floor(Number(amount) * (100 - Number(percent))) / 100;
};

const normalizePromoCode = (value = '') =>
  String(value).trim().toLowerCase().replace(/\s+/g, '').replace(/_/g, '-');

export const resolvePromoOffer = (value = '') => {
  const normalized = normalizePromoCode(value);
  const condensed = normalized.replace(/-/g, '');

  if (
    normalized === MARKET_PRO_OFFER_KEY ||
    condensed === MARKET_PRO_OFFER_KEY.replace(/-/g, '')
  ) {
    return {
      key: MARKET_PRO_OFFER_KEY,
      displayCode: 'Market Pro offer',
      appliedLabel: 'Market Pro offer applied',
      chipLabel: '50% OFF • first 3 months',
      durationLabel: 'for your first 3 months',
      targetPlans: [PRO_PLAN_KEY],
      billingCycle: MONTHLY_BILLING_CYCLE,
      discountPercent: 50,
    };
  }

  if (
    normalized === GET_MAX_50_OFFER_KEY ||
    condensed === GET_MAX_50_OFFER_KEY.replace(/-/g, '')
  ) {
    return {
      key: GET_MAX_50_OFFER_KEY,
      displayCode: 'GETMAX50',
      appliedLabel: 'GETMAX50 applied to MAX monthly',
      chipLabel: '50% OFF • first month',
      durationLabel: 'for your first month',
      targetPlans: [MAX_PLAN_KEY],
      billingCycle: MONTHLY_BILLING_CYCLE,
      discountPercent: 50,
    };
  }

  if (
    normalized === PRO_50_12_OFFER_KEY ||
    condensed === PRO_50_12_OFFER_KEY.replace(/-/g, '')
  ) {
    return {
      key: PRO_50_12_OFFER_KEY,
      displayCode: 'PRO50X12',
      appliedLabel: 'PRO50X12 applied to Pro monthly',
      chipLabel: '50% OFF • 12 months',
      durationLabel: 'for 12 months',
      targetPlans: [PRO_PLAN_KEY],
      billingCycle: MONTHLY_BILLING_CYCLE,
      discountPercent: 50,
    };
  }

  if (
    normalized === HERO_MAX_30_OFFER_KEY ||
    condensed === HERO_MAX_30_OFFER_KEY.replace(/-/g, '')
  ) {
    return {
      key: HERO_MAX_30_OFFER_KEY,
      displayCode: 'HEROMAX30',
      appliedLabel: 'HEROMAX30 applied to MAX monthly',
      chipLabel: '30% OFF • 12 months',
      durationLabel: 'for 12 months',
      targetPlans: [MAX_PLAN_KEY],
      billingCycle: MONTHLY_BILLING_CYCLE,
      discountPercent: 30,
    };
  }

  if (
    normalized === HERO_MAX_50_OFFER_KEY ||
    condensed === HERO_MAX_50_OFFER_KEY.replace(/-/g, '')
  ) {
    return {
      key: HERO_MAX_50_OFFER_KEY,
      displayCode: 'HEROMAX50',
      appliedLabel: 'HEROMAX50 applied to MAX annual',
      chipLabel: '50% OFF • first year',
      durationLabel: 'for your first year',
      targetPlans: [MAX_PLAN_KEY],
      billingCycle: YEARLY_BILLING_CYCLE,
      discountPercent: 50,
    };
  }

  if (
    normalized === ANNUAL_50_OFFER_KEY ||
    condensed === ANNUAL_50_OFFER_KEY.replace(/-/g, '')
  ) {
    return {
      key: ANNUAL_50_OFFER_KEY,
      displayCode: 'ANNUAL50',
      appliedLabel: 'ANNUAL50 applied to annual plans',
      chipLabel: '50% OFF • first year',
      durationLabel: 'for your first year',
      targetPlans: [PRO_PLAN_KEY, MAX_PLAN_KEY],
      billingCycle: YEARLY_BILLING_CYCLE,
      discountPercent: 50,
    };
  }

  return null;
};

export const planSelectionPromoState = ({
  maxTrialMode = false,
  trialIsActive = false,
  offerPresent = false,
  trialEndLabel = null,
} = {}) => {
  if (!maxTrialMode) {
    return {
      mode: 'standard',
      message: null,
    };
  }

  if (trialIsActive) {
    return {
      mode: offerPresent ? 'trial_active_promo' : 'trial_active',
      message: offerPresent
        ? `Promo code saved. It will apply when your MAX trial ends${trialEndLabel ? ` on ${trialEndLabel}` : ''}. You will not be charged today.`
        : `Your MAX trial is active${trialEndLabel ? ` through ${trialEndLabel}` : ''}. Add a promo code now and it will apply when the trial ends. You will not be charged today.`,
    };
  }

  return {
    mode: offerPresent ? 'trial_available_promo' : 'trial_available',
    message: offerPresent
      ? 'Promo code ready. Start the MAX trial for $0 today and the promotion will apply when the trial ends.'
      : 'Have a promo code? Add it before starting the MAX trial. Today is $0, and the promotion applies when the trial ends.',
  };
};

export const trialPromoNeedsConfirmation = ({
  trialIsActive = false,
  linkedOfferKey = null,
  savedOfferKey = null,
} = {}) => (
  !!trialIsActive &&
  !!linkedOfferKey &&
  linkedOfferKey !== savedOfferKey
);
